import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    TextField,
    IconButton,
    InputAdornment,
    CircularProgress,
    Box,
    Paper,
    Typography,
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useAudioInput } from '../../hooks/useAudioInput';
import ConditionalTooltip from '../common/ConditionalTooltip';

const InputArea = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
    padding: theme.spacing(0.5, 2),
    borderRadius: '24px',
    width: '100%',
    boxShadow: theme.shadows[3],
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
        duration: theme.transitions.duration.short,
    }),
    ...(active && {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    }),
    display: 'flex',
    alignItems: 'center',
}));

const ChatInput = ({ sessionId, isSessionReady, isSending, onSendMessage, placeholder, isMobile }) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);
    const theme = useTheme();
    const {
        isRecording,
        isSubmittingAudio,
        handleRecordingStart,
        handleRecordingStop,
        micPermission,
        requestMicPermission,
        progress,
        remainingTime,
        elapsedTime,
    } = useAudioInput(sessionId);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleSend = useCallback(async () => {
        const content = inputValue.trim();
        if (!content || !onSendMessage) return false;
        const success = await onSendMessage(content);
        if (success) {
            setInputValue('');
            if (isMobile) {
                inputRef.current?.blur();
            } else {
                inputRef.current?.focus();
            }
        }
        return success;
    }, [inputValue, onSendMessage, isMobile]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleMicAction = useCallback(async (event) => {
        event.preventDefault();
        if (micPermission === 'prompt' || micPermission === 'denied') {
            await requestMicPermission();
        }
    }, [micPermission, requestMicPermission]);

    // Ensure input is visible when focused on mobile
    const handleFocus = () => {
        if (isMobile && inputRef.current) {
            setTimeout(() => {
                inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300); // Delay to allow keyboard animation to finish
        }
    };

    const hasText = inputValue.trim() !== '';
    const isActive = isRecording || hasText;
    const isActionInProgress = isSending || isSubmittingAudio;
    const isButtonDisabled = !isSessionReady || isActionInProgress;

    const getMicTooltip = () => {
        if (micPermission === 'denied') return "Microphone access denied.";
        if (micPermission === 'prompt') return "Click to enable microphone";
        if (isRecording) return "Release to send";
        return "Press and hold to record";
    };

    const renderMicIcon = () => {
        if (isSubmittingAudio) return <CircularProgress size={24} />;
        if (micPermission === 'granted') {
            let color = 'inherit';
            if (isRecording) {
                color = elapsedTime >= 1000 ? theme.palette.success.main : theme.palette.warning.main;
            }
            return <MicIcon sx={{ color }} />;
        }
        return <MicOffIcon />;
    };

    return (
        <InputArea elevation={0} active={isActive}>
            {isRecording && (
                <>
                    {/* Progress Bar */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: `${progress}%`,
                            height: '100%',
                            background: elapsedTime >= 1000
                                ? `linear-gradient(90deg, ${alpha('#4caf50', 0.8)}, ${alpha('#66bb6a', 0.7)})`
                                : `linear-gradient(90deg, ${alpha('#ff9800', 0.8)}, ${alpha('#ffb74d', 0.7)})`,
                            transition: 'width 50ms linear',
                            zIndex: 1,
                            boxShadow: elapsedTime >= 1000
                                ? 'inset 0 0 20px rgba(76, 175, 80, 0.3)'
                                : 'inset 0 0 20px rgba(255, 152, 0, 0.3)',
                        }}
                    />
                    {/* Background Overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: alpha(theme.palette.background.paper, 0.85),
                            zIndex: 0,
                        }}
                    />
                    {/* Countdown/Timer Text */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                color: remainingTime <= 3
                                    ? '#ffd600'
                                    : elapsedTime >= 1000
                                        ? '#2e7d32'
                                        : '#e65100',
                                fontWeight: 700,
                                textShadow: '0 0 3px #000, 0 0 6px #000',
                                animation: remainingTime <= 3 ? 'flash 0.5s ease-in-out infinite' : 'none',
                                '@keyframes flash': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.3 },
                                },
                            }}
                        >
                            {remainingTime}
                        </Typography>
                    </Box>
                </>
            )}

            <TextField
                id='chat-input-area'
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                placeholder={isRecording ? "" : (placeholder || "Type your message here...")}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onFocus={handleFocus}
                variant="standard"
                disabled={!isSessionReady}
                InputProps={{
                    disableUnderline: true,
                    sx: {
                        py: 1.5,
                        px: 1,
                        fontSize: '1rem',
                    },
                    endAdornment: (
                        <InputAdornment position="end" sx={{ height: 'auto', zIndex: 2 }}>
                            <ConditionalTooltip title={hasText ? "Send message" : getMicTooltip()}>
                                <span>
                                    <IconButton
                                        color={hasText ? "primary" : "inherit"}
                                        onClick={hasText ? handleSend : (micPermission !== 'granted' ? handleMicAction : undefined)}
                                        onMouseDown={!hasText && micPermission === 'granted' ? handleRecordingStart : undefined}
                                        onMouseUp={!hasText && micPermission === 'granted' ? handleRecordingStop : undefined}
                                        onMouseLeave={!hasText && micPermission === 'granted' ? handleRecordingStop : undefined}
                                        onTouchStart={!hasText && micPermission === 'granted' ? handleRecordingStart : undefined}
                                        onTouchEnd={!hasText && micPermission === 'granted' ? handleRecordingStop : undefined}
                                        disabled={isButtonDisabled}
                                        sx={{
                                            padding: theme.spacing(1.5, 3),
                                            marginRight: -1.5,
                                            borderRadius: '24px',
                                            bgcolor: hasText ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                            '&:hover': { bgcolor: hasText ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.active, 0.1) }
                                        }}
                                    >
                                        {hasText ? (isSending ? <CircularProgress size={24} /> : <SendIcon />) : renderMicIcon()}
                                    </IconButton>
                                </span>
                            </ConditionalTooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </InputArea>
    );
};

ChatInput.propTypes = {
    sessionId: PropTypes.string,
    isSessionReady: PropTypes.bool.isRequired,
    isSending: PropTypes.bool,
    onSendMessage: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    isMobile: PropTypes.bool,
};

export default ChatInput;